import { Trans, useTranslation } from 'react-i18next'
import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import zionDebug from '@fs/zion-debug'
import { useFeatureFlags } from '@fs/zion-flags'
import { Alert, Button, Paragraph, FlowGrid, useOverlay } from '@fs/zion-ui'
import { PersonRemove } from '@fs/zion-icon'
import ZionForm, { useFieldArray, useZionForm, useWatch, useFormState } from '@fs/zion-form'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  getNestedOrFloatingElementById,
  getPersonName,
} from '@fs/zion-sls'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  prepareRelationshipsDefaultValues,
  getGivenName,
  toSavableFormData,
  buildAddedMemberRow,
} from './utils/relationship.utils'
import { getHouseholdSuggestions } from '../shared/relationships/relationshipQueries'
import {
  getBreakingRelationships,
  getFormValuesAfterMoveOut,
  getMoveOutOfGroupDispatches,
} from './utils/moveOutOfGroup'
import CardFooter from '../shared/components/CardFooter'
import getRelationshipDispatches, {
  getPersonIdsToReviewFromCreateActions,
} from '../shared/relationships/saveRelationships'
import { MAKE_NEW_RECORD } from '../shared/relationships/relationshipConstants'
import { FullTextApp, headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { collectHighlightIdsForRelationships } from '../../../views/indexing/components/editPanels/editRelationships/utils/EditRelationships.utls'
import { householdDetailsValidationSchema } from '../shared/relationships/validationSchema'
import RelationshipInputs from './components/RelationshipInputs'
import AddGroupMembersFlow from './components/AddGroupMembersFlow'
import MoveOutOfGroupDialogOverlay from './components/MoveOutOfGroupDialogOverlay'

// To turn this on, in the browser console: localStorage.debug = 'fs:rip:relationshipsCard'
const debug = zionDebug('rip:relationshipsCard')

/**
 * Editable form for the Relationships card: one relationship control per other group member, plus
 * the affordances for growing and shrinking the group.
 *
 * SAVE governs relationship values only — adds and removes apply as the patron makes them, and a
 * relationship value is discarded by CANCEL. Removing the viewed person closes the card and
 * discards any staged edit, since the move removes the very relationships that edit describes;
 * removing another member leaves the card open.
 *
 * @component
 * @param {Object}   props
 * @param {Object}   props.person                       The primary person SLS person object (must include `id` and `recordId`).
 * @param {Array}    props.allRecords                   Array of all RECORD type SLS objects in the current image.
 * @param {Object}   [props.record]                     The group the viewed person belongs to.
 * @param {Array}    props.relationships                Array of existing relationship objects used for defaults.
 * @param {boolean}  [props.reviewed=false]             Whether the relationships section has been reviewed.
 * @param {Function} [props.setReviewed]                Sets parent reviewed state, once on first successful save.
 * @param {Function} props.setIsEditing                 Toggles the parent card edit mode off after save / cancel.
 * @param {string}   props.userId                       Current user id for audit purposes passed to save.
 * @param {Function} [props.onSave]                     Callback invoked after successful save with form data.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] Callback function invoked when the form dirty state changes.
 * @param {boolean}  [props.saveCardDataToSls=false]    Whether to save changes to SLS backend.
 * @param {string}   [props.appName=FullTextApp]        The application name for SLS operations.
 * @param {string}   [props.splitScreenPartitionKey]    Split screen partition key if using split screen.
 * @param {boolean}  [props.isCreatingNewPerson=false]  Whether the card is being used in the context of creating a new person (vs editing an existing one).
 * @param {boolean}  [props.disableGroupChanges=false] Hides the affordances that grow or shrink the group,
 *                                                     for callers the patron already reached through one.
 * @param {Function} [props.onAddExistingPerson]        Handler for "Add existing person".
 * @param {Function} [props.onAddNewPerson]             Handler for "Add new person".
 *
 * @returns {JSX.Element}
 */
const RelationshipsCardEditMode = ({
  person,
  allRecords,
  record,
  relationships,
  reviewed = false,
  setReviewed = () => {},
  setIsEditing,
  userId,
  onSave = () => {},
  onDirtyChange = () => {},
  saveCardDataToSls = false,
  splitScreenPartitionKey,
  appName = FullTextApp,
  isCreatingNewPerson = false,
  disableGroupChanges = false,
  onAddExistingPerson,
  onAddNewPerson,
}) => {
  const [t] = useTranslation()
  const { types, images, currentImageArk } = useSlsStateContext()
  const h = useSlsHighlightsContext()
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])

  const householdSuggestions = useMemo(() => {
    return getHouseholdSuggestions({ records: allRecords, person })
  }, [allRecords, person])

  const [saving, setSaving] = useState(false)
  // A move reports progress in its own dialog, so it must leave SAVE/CANCEL alone.
  const [movingOut, setMovingOut] = useState(false)
  const [error, setError] = useState(null)
  const [personIdToMoveOut, setPersonIdToMoveOut] = useState(null)
  const slsDispatch = useSlsDispatchContext()
  const { topMostPanel } = useOverlayContext()
  const { setDisplayHighlights, getHighlights } = useSlsHighlightsContext()

  // Focus returns here after a move-out, so it never lands on a control that the move removed.
  const addGroupMembersRef = useRef(null)
  const moveOutOverlay = useOverlay({ focusBackRef: addGroupMembersRef })

  const defaultValues = useMemo(() => {
    return prepareRelationshipsDefaultValues(relationships, person, householdSuggestions, isCreatingNewPerson)
  }, [relationships, person, householdSuggestions, isCreatingNewPerson])

  const formMethods = useZionForm({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(householdDetailsValidationSchema),
  })

  const fieldArrayMethods = useFieldArray({
    name: 'relationships',
    control: formMethods.control,
  })

  const { fields, append, remove } = fieldArrayMethods

  const watchedFormValues = useWatch({
    control: formMethods.control,
  })

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: formMethods.control })

  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])

  const personName = useMemo(() => getPersonName({ person }), [person])
  const givenName = useMemo(() => getGivenName(person), [person])

  const visibleFields = useMemo(() => fields.filter((f) => !f.isDeleted), [fields])

  // Watched values rather than `fields`, which does not re-render when a row's person changes, so a
  // name staged but not yet saved still counts as in the group.
  const groupMemberIds = useMemo(
    () =>
      (watchedFormValues?.relationships ?? [])
        .filter((relationship) => !relationship?.isDeleted && relationship?.personId)
        .map((relationship) => relationship.personId),
    [watchedFormValues?.relationships]
  )
  /**
   * Whether a row is an add staged here rather than somebody the group already had.
   *
   * Read from the defaults, which are rebuilt from SLS: a person absent from them is not in the group
   * at all yet, whatever the form currently shows.
   *
   * @param {string} personId - The person on the row.
   * @returns {boolean}
   */
  const isStagedMember = useCallback(
    (personId) => !(defaultValues?.relationships ?? []).some((relationship) => relationship?.personId === personId),
    [defaultValues]
  )

  // Only these move in the same save as a later add, so only these keep a relationship with one.
  const pendingMemberIds = useMemo(
    () => groupMemberIds.filter((personId) => isStagedMember(personId)),
    [groupMemberIds, isStagedMember]
  )

  const isOnlyMember = visibleFields.length === 0

  const personToMoveOut = useMemo(
    () => (personIdToMoveOut ? getNestedOrFloatingElementById(images, personIdToMoveOut) : null),
    [personIdToMoveOut, images]
  )

  const breakingRelationships = useMemo(
    () => getBreakingRelationships({ person: personToMoveOut, record, images }),
    [personToMoveOut, record, images]
  )

  /**
   * Persist dispatches. Shared by the SAVE and move-out paths so they behave identically.
   *
   * @param {Object} params
   * @param {Array<Object>} params.allDispatches - SLS dispatches to persist.
   * @param {Object} params.formValues - Values handed back to `onSave`.
   * @param {boolean} [params.closeCard=false] - Leave edit mode once persisted.
   * @param {boolean} [params.notifyParent=true] - Call `onSave`. The consumer's handler closes the
   *   card, so a move that must leave it open passes false.
   * @param {Function} [params.setPending=setSaving] - Which pending flag this commit owns.
   * @param {Function} [params.onCommitted] - Runs only once the write has landed.
   * @param {Function} [params.onSettled] - Runs once the commit resolves, either way.
   * @returns {void}
   */
  const commitDispatches = useCallback(
    ({
      allDispatches,
      formValues,
      closeCard = false,
      notifyParent = true,
      setPending = setSaving,
      onCommitted,
      onSettled,
    }) => {
      const personIdsToReview = getPersonIdsToReviewFromCreateActions(allDispatches)
      const sls = generateSls(allDispatches)

      // The last look at what the backend is asked to do, for SAVE and move-out alike.
      debug('dispatches to persist', { allDispatches, sls, saveCardDataToSls })

      if (!saveCardDataToSls) {
        setPending(false)
        if (!reviewed) setReviewed(true)
        onCommitted?.()
        if (notifyParent) onSave({ dispatches: allDispatches, sls, formValues, personIdsToReview })
        h.commitEdits()
        if (closeCard) setIsEditing(false)
        onSettled?.()
        return
      }

      saveSlsAndUpdateState({
        sls,
        dispatches: allDispatches,
        saveChangesFlag,
        slsActionsSplunkDebugger,
        userId,
        dispatch: slsDispatch,
        appName,
        fsUserAgentChain: headers['FS-User-Agent-Chain'],
        errorHandler: (err) => {
          setError(err)
          errorCollector(err, null, {
            codeId: 90,
            sls,
            status: err?.response?.status,
            responseBody: err?.response?.data,
          })
          setPending(false)
          onSettled?.()
        },
        successHandler: () => {
          // Update local state even when the saveChangesFlag is off.
          if (!saveChangesFlag.isOn) {
            slsDispatch({ type: 'BATCH_DISPATCHES', value: allDispatches })
          }

          setPending(false)
          if (!reviewed) setReviewed(true)
          onCommitted?.()
          // The AddNamesPanel needs personIdsToReview to add newly created people to the review list.
          if (notifyParent) onSave({ formValues, personIdsToReview })
          h.commitEdits()
          if (closeCard) setIsEditing(false)
          onSettled?.()
        },
        saveInitiatorFilename: 'RelationshipsCardEditMode.js',
      })
    },
    [
      appName,
      h,
      onSave,
      reviewed,
      saveCardDataToSls,
      saveChangesFlag,
      setIsEditing,
      setReviewed,
      slsActionsSplunkDebugger,
      slsDispatch,
      userId,
    ]
  )

  /**
   * Build the relationship dispatches for the current form state.
   *
   * @param {Object} data - Current form values.
   * @returns {Promise<Array<Object>>} Relationship and highlight dispatches.
   */
  const buildRelationshipDispatches = useCallback(
    async (data) => {
      const newPersonAndRelationshipDispatches = await getRelationshipDispatches({
        // Only the in-flight `relationships[i].relationship` shape differs from what save reads.
        formData: toSavableFormData(data),
        defaultValues,
        person,
        images,
        appName,
        reviewDispatches: [],
        currentPartitionKey: currentImageArk,
      })

      const highlightDispatches = h.getDispatches({
        mainImageArk: currentImageArk,
        splitScreenImageArk: splitScreenPartitionKey,
      })

      return [...newPersonAndRelationshipDispatches, ...highlightDispatches]
    },
    [appName, currentImageArk, defaultValues, h, images, person, splitScreenPartitionKey]
  )

  const saveDispatches = useCallback(
    async (data) => {
      setSaving(true)
      setError(null)

      try {
        const allDispatches = await buildRelationshipDispatches(data)
        commitDispatches({ allDispatches, formValues: data })
      } catch (err) {
        // Validation errors (duplicate relationships, missing data, etc.)
        setError(err)
        errorCollector(err, null, { codeId: 91, message: 'Relationship validation error' })
        setSaving(false)
      }
    },
    [buildRelationshipDispatches, commitDispatches]
  )

  const onSubmit = useCallback(
    async (data) => {
      // Ensure household has a value - default to MAKE_NEW_RECORD if cleared
      if (!data?.household?.key) {
        const defaultHousehold = householdSuggestions.find((hh) => hh.key === MAKE_NEW_RECORD)
        if (defaultHousehold) {
          data = { ...data, household: defaultHousehold, targetRecordId: defaultHousehold.key }
          formMethods.setValue('household', defaultHousehold, { shouldDirty: true })
          formMethods.setValue('targetRecordId', defaultHousehold.key, { shouldDirty: true })
        }
      }

      // Only proceed if form is dirty OR person is floating
      if (isDirty) {
        // Auto-assign primary for single-person household
        const activeRelationships = data.relationships.filter((rel) => !rel.isDeleted && rel.personId)
        if (activeRelationships.length === 0 && !data.targetPersonIsPrimary) {
          formMethods.setValue('targetPersonIsPrimary', true, { shouldDirty: true })
          data = { ...data, targetPersonIsPrimary: true }
        }

        await saveDispatches(data)
      } else {
        setSaving(false)
        onSave({ formValues: data })
        h.commitEdits()
      }
    },
    [isDirty, formMethods, saveDispatches, setSaving, onSave, h, householdSuggestions]
  )

  /**
   * Stage everybody the Add Existing Person sheet came back with as rows on the card.
   *
   * Staged rather than written: unlike a move out of the group, an add is not settled until the
   * patron has said how each new member is related, so it rides along with SAVE. Until then the
   * card is dirty and CANCEL discards the add, which is what the design shows.
   *
   * @param {import('../panels/addExistingPersonPanel/useAddExistingPersonFlow').AddExistingPersonResult} result
   *   What the patron agreed to. Only `personIds` is applied here; the move itself deletes the moved
   *   person's relationships (see `movePersonToTargetRecord` in `saveRelationships.js`).
   * @returns {void}
   */
  const handleAddGroupMembers = useCallback(
    ({ personIds = [] } = {}) => {
      // The save path throws on a duplicate, so guard against a stale selection racing a move-out.
      const rows = personIds
        .filter((personId) => personId && !groupMemberIds.includes(personId))
        .map((personId) => {
          const addedPerson = getNestedOrFloatingElementById(images, personId)
          return buildAddedMemberRow({
            personId,
            personName: addedPerson ? getPersonName({ person: addedPerson }) : '',
            recordId: addedPerson?.recordId ?? null,
          })
        })

      debug('staging added group members', {
        requestedPersonIds: personIds,
        stagedPersonIds: rows.map((row) => row.personId),
        alreadyInGroup: personIds.filter((personId) => groupMemberIds.includes(personId)),
      })

      if (rows.length > 0) append(rows)
    },
    [append, groupMemberIds, images]
  )

  const handleRemoveFromGroup = useCallback(
    (index) => {
      const personId = formMethods.getValues(`relationships[${index}].personId`)

      // Never saved, so there is no group to move them out of — and a move-out here would write one.
      if (isStagedMember(personId)) {
        remove(index)
        return
      }

      setPersonIdToMoveOut(personId)
      moveOutOverlay.open()
    },
    [formMethods, moveOutOverlay, isStagedMember, remove]
  )

  const handleMoveSelfOutOfGroup = useCallback(() => {
    setPersonIdToMoveOut(person.id)
    moveOutOverlay.open()
  }, [person.id, moveOutOverlay])

  /**
   * Apply a confirmed move out of the group, immediately rather than on SAVE. A staged edit for the
   * person moving out is discarded — the move removes the relationships it describes. The dialog
   * stays open and pending until the commit resolves, so it reports its own progress.
   *
   * @returns {Promise<void>}
   */
  const handleConfirmMoveOut = useCallback(async () => {
    if (!personToMoveOut) return

    setMovingOut(true)
    setError(null)

    const settle = () => {
      setMovingOut(false)
      moveOutOverlay.close()
      setPersonIdToMoveOut(null)
    }

    try {
      const isSelf = personToMoveOut.id === person.id
      const formValues = formMethods.getValues()

      const moveDispatches = await getMoveOutOfGroupDispatches({
        person: personToMoveOut,
        record,
        currentPartitionKey: currentImageArk,
      })

      commitDispatches({
        allDispatches: moveDispatches,
        formValues,
        // On success only: a failed write must leave the member listed. reset also rebaselines
        // isDirty, so a committed move stops looking unsaved.
        onCommitted: isSelf
          ? undefined
          : () => formMethods.reset(getFormValuesAfterMoveOut({ formValues, movedPersonId: personToMoveOut.id })),
        closeCard: isSelf,
        // The consumer's onSave closes the card, which only a self-removal should do.
        notifyParent: isSelf,
        setPending: setMovingOut,
        onSettled: settle,
      })
    } catch (err) {
      setError(err)
      errorCollector(err, null, { codeId: 91, message: 'Move out of group error' })
      settle()
    }
  }, [commitDispatches, currentImageArk, formMethods, moveOutOverlay, person.id, personToMoveOut, record])

  const handleCancel = useCallback(() => {
    formMethods.reset()
    onDirtyChange(false)
    setIsEditing(false)
  }, [formMethods, setIsEditing, onDirtyChange])

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (topMostPanelId !== 'highlightsPanel' && currentImageArk) {
      const highlights = getHighlights()
      const { allPeopleHighlights, allPeopleReferenceHighlights } = collectHighlightIdsForRelationships(
        watchedFormValues,
        person.id,
        highlights.mainEditHighlights,
        highlights.splitScreenEditHighlights
      )

      setDisplayHighlights({
        highlights: [...allPeopleHighlights, ...allPeopleReferenceHighlights],
        mainPartitionKey: currentImageArk,
        splitScreenPartitionKey,
        fieldTypes: ['NAME_SURN', 'NAME_SUF', 'NAME_GN'],
        customFilter: (highlight) => highlight.personIds.length > 0,
      })
    }
  }, [
    currentImageArk,
    splitScreenPartitionKey,
    watchedFormValues,
    person.id,
    topMostPanelId,
    setDisplayHighlights,
    getHighlights,
  ])

  return (
    <ZionForm methods={formMethods} onSubmit={onSubmit} id="household-card-form">
      <FlowGrid orientation="vertical" columnCount={1} gutters="xxs">
        {isOnlyMember ? (
          <Paragraph secondary size="sm" data-testid="relationships-no-other-members">
            {t('relationships-card.no-other-members', 'No additional people in this group.')}
          </Paragraph>
        ) : (
          <Paragraph size="sm">
            <Trans
              i18nKey="relationships-card.relationships-description"
              values={{ personName }}
              components={{ bold: <b /> }}
            >
              The following people are in this group. How are they related to <bold>{personName}</bold>?
            </Trans>
          </Paragraph>
        )}

        {fields.map((field, index) => (
          <RelationshipInputs
            key={field?.id}
            field={field}
            index={index}
            memberName={field?.value?.primaryText}
            memberPerson={getNestedOrFloatingElementById(images, field?.personId)}
            personName={personName}
            types={types}
            onRemoveFromGroup={handleRemoveFromGroup}
          />
        ))}

        {!disableGroupChanges && (
          <div ref={addGroupMembersRef}>
            <AddGroupMembersFlow
              person={person}
              personName={personName}
              groupMemberIds={groupMemberIds}
              pendingMemberIds={pendingMemberIds}
              onAddExistingPerson={onAddExistingPerson}
              onAddNewPerson={onAddNewPerson}
              onSave={handleAddGroupMembers}
            />
          </div>
        )}

        {!isOnlyMember && !disableGroupChanges && (
          <Button
            emphasis="low"
            Icon={PersonRemove}
            onClick={handleMoveSelfOutOfGroup}
            linkName="move-out-of-group"
            data-testid="move-self-out-of-group-button"
          >
            {t('relationships-card.move-out-of-group', 'Move {givenName} out of group', { givenName })}
          </Button>
        )}

        <CardFooter reviewed={reviewed} onCancel={handleCancel} saving={saving} label="family-members" />
        {error && (
          <Alert type="error">
            {t('household-details-card.save-error-msg', 'Error saving data. Please try again later.')}
          </Alert>
        )}

        <MoveOutOfGroupDialogOverlay
          overlay={moveOutOverlay}
          personName={getPersonName({ person: personToMoveOut }) || ''}
          breakingRelationships={breakingRelationships}
          onConfirm={handleConfirmMoveOut}
          moving={movingOut}
        />
      </FlowGrid>
    </ZionForm>
  )
}

RelationshipsCardEditMode.propTypes = {
  person: PropTypes.shape({
    // Required properties
    id: PropTypes.string.isRequired,
    // recordId can be null for floating persons (persons not yet attached to a record)
    recordId: PropTypes.string,
  }),

  relationships: PropTypes.arrayOf(
    PropTypes.shape({
      relKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.oneOf([null])]),
      otherPersonId: PropTypes.string.isRequired,
    })
  ),

  // eslint-disable-next-line react/forbid-prop-types
  record: PropTypes.object,
  reviewed: PropTypes.bool,
  saveCardDataToSls: PropTypes.bool,
  splitScreenPartitionKey: PropTypes.string,
  disableGroupChanges: PropTypes.bool,
  onAddExistingPerson: PropTypes.func,
  onAddNewPerson: PropTypes.func,
}

RelationshipsCardEditMode.defaultProps = {
  person: { id: '', recordId: null },
  relationships: [],
  record: null,
  reviewed: false,
  saveCardDataToSls: false,
  disableGroupChanges: false,
  onAddExistingPerson: undefined,
  onAddNewPerson: undefined,
}

export default RelationshipsCardEditMode
